/**
 * The PageID pill (SPEC.md §12.3): the geometry and the message shapes shared by the host script
 * (`pageid-label.ts`, on the customer's page) and the frame (`frame.ts`, PageID's origin), plus the small pure
 * rules (the swipe, the spring easing) the frame uses. No DOM here, so it is unit-tested in Node.
 *
 * The iframe sits fixed at the top of the host page in one of three boxes:
 *  - `pill`: the expanded pill, centred (its width + a shadow margin);
 *  - `icon`: the small round icon, top right;
 *  - `stage`: the whole top strip while the pill morphs into the icon or back (pointer-events: none, so the
 *    strip never takes the host page's clicks).
 * The frame draws everything in "stage coordinates" (x from the host viewport's left edge) and shifts its own
 * content by the box's left edge, so a box change never moves anything on screen.
 */

export type PillMode = 'pill' | 'icon' | 'stage' | 'hidden';

/** Shadow margin around the pill / icon inside the iframe box. */
export const MARGIN = 14;
/** Gap between the top of the viewport (after the safe area) and the pill. */
export const TOP = 8;
/** Extra room under the pill for its shadow. */
export const BOTTOM = 22;
export const ICON = 48;
export const PILL_MAX = 400;
/** The icon's gap to the right edge of the viewport (after the safe area), outside its margin. */
export const ICON_RIGHT = 6;

/** Timings (ms). Reduced motion: a 200 ms fade, nothing else moves. */
export const T = {
  entrance: 560,
  checkStart: 240,
  check: 640,
  reveal: 1180,
  autoCollapse: 5000,
  autoCollapseAgain: 6000,
  autoCollapseWarn: 7000,
  morph: 460,
  fade: 200,
  longPress: 550,
  sessionMs: 10 * 60_000,
} as const;

/** 16 px gutters on a phone (the box, with its shadow margin, stays 4 px narrower than the viewport). */
export const pillWidth = (vw: number): number => Math.max(220, Math.min(PILL_MAX, Math.floor(vw) - 32));

export type Box = { w: number; h: number; left: number };

/** The iframe box (host coordinates) for a mode. `ph` = the pill's visual height, `ir` = the right safe-area inset. */
export function boxFor(mode: Exclude<PillMode, 'hidden'>, vw: number, ir: number, ph: number): Box {
  const h = TOP + Math.max(ph, ICON) + BOTTOM;
  if (mode === 'stage') return { w: Math.floor(vw), h, left: 0 };
  if (mode === 'icon') {
    const w = ICON + 2 * MARGIN;
    return { w, h: TOP + ICON + BOTTOM, left: Math.floor(vw - ir - ICON_RIGHT - w) };
  }
  const w = pillWidth(vw) + 2 * MARGIN;
  return { w, h: TOP + ph + BOTTOM, left: Math.floor((vw - w) / 2) };
}

/** Where the pill and the icon are drawn, in stage coordinates. */
export function rects(vw: number, ir: number, ph: number) {
  const pill = boxFor('pill', vw, ir, ph);
  const icon = boxFor('icon', vw, ir, ph);
  return {
    pill: { x: pill.left + MARGIN, y: TOP, w: pillWidth(vw), h: ph },
    icon: { x: icon.left + MARGIN, y: TOP, w: ICON, h: ICON },
  };
}

/** Which box the frame is in, from its own width (the widths never coincide: the pill box is ≤ vw − 4). */
export function modeFromWidth(width: number, vw: number, ir: number, ph: number): Exclude<PillMode, 'hidden'> {
  const near = (a: number, b: number) => Math.abs(a - b) < 1.5;
  if (near(width, boxFor('icon', vw, ir, ph).w)) return 'icon';
  if (near(width, boxFor('pill', vw, ir, ph).w)) return 'pill';
  return 'stage';
}

// ---- the messages across the frame boundary ------------------------------------------------------------
// frame → host: only a mode and a box size. Never the symbol, the phrase, the company or the state.
export type FrameMessage = { pageidPill: 1; mode: PillMode; w: number; h: number };
// host → frame: the viewport width and the right safe-area inset (harmless numbers: a lie only misplaces the pill).
export type HostMessage = { pageidHost: 1; vw: number; ir: number };

const num = (v: unknown, lo: number, hi: number): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? v : null);

/** The host's check of a frame message (after the origin and source checks). Anything else is ignored. */
export function parseFrameMessage(d: unknown): FrameMessage | null {
  if (!d || typeof d !== 'object' || (d as { pageidPill?: unknown }).pageidPill !== 1) return null;
  const m = d as Record<string, unknown>;
  if (m.mode !== 'pill' && m.mode !== 'icon' && m.mode !== 'stage' && m.mode !== 'hidden') return null;
  const w = num(m.w, 0, 4000);
  const h = num(m.h, 0, 640);
  if (w === null || h === null) return null;
  const keys = Object.keys(m).sort().join(',');
  if (keys !== 'h,mode,pageidPill,w') return null;
  return { pageidPill: 1, mode: m.mode, w, h };
}

export function parseHostMessage(d: unknown): HostMessage | null {
  if (!d || typeof d !== 'object' || (d as { pageidHost?: unknown }).pageidHost !== 1) return null;
  const m = d as Record<string, unknown>;
  const vw = num(m.vw, 200, 8000);
  const ir = num(m.ir, 0, 200);
  return vw === null || ir === null ? null : { pageidHost: 1, vw, ir };
}

// ---- the swipe -----------------------------------------------------------------------------------------
/** Pointer movement before a press counts as a drag, not a tap. */
export const DRAG_SLOP = 6;

/**
 * Swipe up to dismiss: dy < 0 is up. Dismissed when dragged up past 24 px (or a third of the pill), or flicked
 * up faster than 0.45 px/ms; a downward drag never dismisses.
 */
export function swipeDismisses(dy: number, vy: number, ph: number): boolean {
  if (dy > 0) return false;
  return -dy > Math.min(24, ph / 3) || vy < -0.45;
}

/** The drag's visual offset: free upwards, a stiff rubber band downwards. */
export const dragOffset = (dy: number): number => (dy < 0 ? dy : 8 * (1 - Math.exp(-dy / 30)));

// ---- the spring ----------------------------------------------------------------------------------------
/**
 * A damped spring as a CSS `linear()` easing: ζ = 0.78 overshoots by ≈ 2 %, a tasteful settle without bounce.
 * Browsers without `linear()` get the cubic-bezier fallback.
 */
export function springEasing(zeta = 0.78, points = 24): string {
  const w = 2 * Math.PI * 1.15;
  const wd = w * Math.sqrt(1 - zeta * zeta);
  const at = (t: number) => 1 - Math.exp(-zeta * w * t) * (Math.cos(wd * t) + ((zeta * w) / wd) * Math.sin(wd * t));
  const out: string[] = [];
  for (let i = 0; i <= points; i++) {
    const t = i / points;
    out.push(i === points ? '1' : (Math.round(at(t) * 1000) / 1000).toString());
  }
  return `linear(${out.join(', ')})`;
}
export const SPRING_FALLBACK = 'cubic-bezier(.2, 1.08, .32, 1)';
/** iOS-like ease for the morph. */
export const MORPH_EASE = 'cubic-bezier(.32, .72, 0, 1)';
